import React, { useState, useEffect, useContext } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Layout, Spin, message, notification } from 'antd';
import { BellFilled } from '@ant-design/icons';
import API from '../../api/API';
import { sendPageViewAnalytics } from '../../analytics/analytics';
import { ColorBar, TopNav, SideNav } from './index';
import Home from '../pages/home/Home';
import { Event } from '../pages/event';
import { ViewAllEvents } from '../pages/viewAllEvents';
import { UserProfile, AccountSettings } from '../pages/userProfile';
import Notifications from '../pages/notifications/Notifications';
import Help from '../pages/help/Help';
import HaroldSpeakingEngagements from '../pages/haroldSpeakingEngagement/HaroldSpeakingEngagements';
import NotFound from '../errors/NotFound';
import { FormDataContext, UserContext } from '../../contexts';
import { IStaff } from '../../models/staff';
import { IState } from '../../models/state';
import { IAgency } from '../../models/agency';
import { IAudience } from '../../models/audience';
import { IFormat } from '../../models/format';
import { IFormData } from '../../models/formData';
import './Dashboard.less';
import config from '../../config';
import { INotification } from '../../models/notification';

const { Header, Sider, Content } = Layout;
const channel = new BroadcastChannel('notification-channel');

const Dashboard: React.FC = () => {
  // #region Setting Up State

  const { user } = useContext(UserContext);
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<IFormData | any>({});
  const history = useHistory();

  // #endregion

  // #region UseEffect And LifeCycle Methods

  /*
   * The useEffect method that updates the current document title.
   */
  useEffect(() => {
    return () => {
      document.title = 'EHE Tracker';
    };
  }, []);

  /*
   * The useEffect method that listens to changes in browser history to
   * send to Google Analytics.
   */
  useEffect(() => {
    history.listen((location) => {
      sendPageViewAnalytics(location.pathname, location.search);
    });
  }, [history]);

  /*
   * The useEffect method to show in app notifications.
   */
  useEffect(() => {
    const markNotificationAsRead = (selectedNotification: INotification) => {
      const updatedNotification = { ...selectedNotification, read: true };
      API.post(`/notification/${user.username}`, [updatedNotification]);
    };

    channel.onmessage = (event) => {
      const data = event.data as INotification;

      notification.open({
        message: data.message,
        icon: <BellFilled style={{ color: '#1c75bd' }} />,
        key: `notification-${data.id}`,
        top: 74,
        style: { cursor: 'pointer' },
        onClick: () => {
          notification.close(`notification-${data.id}`);
          markNotificationAsRead(data);
          history.push(`/events/${data.action.eventId}`);
        },
      });
    };
  }, [history, user.username]);

  /*
   * The useEffect method that sends an API request to the server to
   * get the values to populate the form fields.
   *
   * Once the response is received, we are separating the staff based
   * on whether they are 'principal', 'agency' or 'region'. Upon completion,
   * we update the component formData state.
   */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const reqStates = API.get('/location/state');
      const reqFormats = API.get('/format');
      const reqAgencies = API.get('/agency');
      const reqAudiences = API.get('/audience');
      const reqStaff = API.get('/staff');
      const reqJurisdictions = API.get('/location/jurisdiction');

      const [
        respStates,
        respFormats,
        respAgencies,
        respAudiences,
        respStaff,
        respJurisdictions,
      ] = await Promise.all([
        reqStates,
        reqFormats,
        reqAgencies,
        reqAudiences,
        reqStaff,
        reqJurisdictions,
      ]);

      const respPrincipal: IStaff = respStaff.data
        .filter((s: IStaff) => s.orgLevel === 'principal')
        .sort((left: IStaff, right: IStaff) =>
          left.name < right.name ? -1 : 1,
        );

      const respAdditional: IStaff = respStaff.data.filter(
        (s: IStaff) => s.orgLevel === 'agency',
      );
      const respRegion: IStaff = respStaff.data.filter(
        (s: IStaff) => s.orgLevel === 'region',
      );

      const respEventFormData = {
        states: respStates.data as IState,
        formats: respFormats.data as IFormat,
        agencies: respAgencies.data as IAgency,
        audiences: respAudiences.data as IAudience,
        principal: respPrincipal,
        additional: respAdditional,
        region: respRegion,
        jurisdictions: respJurisdictions.data as IState,
      };

      setFormData(respEventFormData);
      setLoading(false);
    };

    try {
      fetchData();
    } catch {
      setLoading(true);
      message.error('Looks like something went wrong.');
    }
  }, []);

  // #endregion

  // #region Component Methods

  /*
   * This method handles updating the component state of whether or
   * not the SideNav component is collapsed.
   */
  function handleCollapse(currentState: boolean) {
    setCollapsed(!currentState);
  }

  // #endregion

  // #region Render Section

  return (
    <>
      <ColorBar />
      <Layout>
        <Sider
          width={180}
          collapsedWidth={45}
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="Dashboard-sider"
        >
          <SideNav collapsed={collapsed} setCollapsed={handleCollapse} />
        </Sider>
        <Layout>
          <Header
            className="Dashboard-header"
            style={collapsed ? { width: 'calc(100vw - 45px)' } : {}}
          >
            <TopNav collapsed={collapsed} />
          </Header>
          <Content className="Dashboard-content">
            {loading ? (
              <Spin size="large" className="Dashboard-loading" />
            ) : (
              /*
               * Once the dashboard component has finished fetching the formData, we
               * are passing that data through the FormDataContext to all the components
               * in our Switch routes.
               */
              <FormDataContext.Provider value={formData}>
                <Switch>
                  <Route exact path="/" component={Home} />
                  <Route exact path="/submit" component={Event} />
                  <Route
                    exact
                    path="/events/:eventId?"
                    component={ViewAllEvents}
                  />
                  <Route exact path="/profile" component={UserProfile} />
                  <Route
                    exact
                    path="/profile/settings"
                    component={AccountSettings}
                  />
                  <Route
                    exact
                    path="/edit/:id"
                    render={() => (
                      <Event isEdit key={window.location.pathname} />
                    )}
                  />
                  <Route exact path="/help" component={Help} />
                  <Route
                    exact
                    path="/notifications"
                    component={Notifications}
                  />
                  {config.env.hostname.includes(window.location.hostname) && (
                    <Route
                      exact
                      path="/harold"
                      component={HaroldSpeakingEngagements}
                    />
                  )}
                  <Route component={NotFound} />
                </Switch>
              </FormDataContext.Provider>
            )}
          </Content>
        </Layout>
      </Layout>
    </>
  );

  // #endregion
};

export default Dashboard;
